import { Eye, Package } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Carousel, Spin, Typography } from "antd";
import { useGetMyServiceTickets } from "~/services/service-ticket";
import { TInventory } from "~/types/inventory";
import {
  InventoryStatusCode,
  InventoryType,
  PackingTypes,
  SortOrder,
} from "~/utils/constants";
import { capitalize, formatDimensions } from "~/utils";
import { SortByField } from "~/services/inventory";
import "./inventory.css";

const { Title, Text } = Typography;

export const Inventory: React.FC = () => {
  const [selectedInventory, setSelectedInventory] = useState<TInventory | null>(null);
  const [sortBy, setSortBy] = useState<SortByField | string>(SortByField.INVENTORY_REF);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);

  // Fetch all service tickets for the logged-in staff member
  const { data: serviceTickets, isLoading } = useGetMyServiceTickets({});

  const handleSelectInventory = (inventory: TInventory) => {
    setSelectedInventory(inventory);
  };

  const handleSort = (field: SortByField | string) => {
    if (sortBy === field) {
      setSortOrder(
        sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      );
    } else {
      setSortBy(field);
      setSortOrder(SortOrder.ASC);
    }
  };

  // Extract all unique inventory items from service tickets
  const allInventories = useMemo(() => {
    if (!serviceTickets?.items) return [];

    const uniqueInventories = new Map();
    serviceTickets.items.forEach((ticket) => {
      (ticket.inventories ?? []).forEach((inventory) => {
        if (!uniqueInventories.has(inventory.id)) {
          uniqueInventories.set(inventory.id, inventory);
        }
      });
    });

    const inventories = Array.from(uniqueInventories.values());

    return inventories.sort((a, b) => {
      const aValue = a[sortBy] || "";
      const bValue = b[sortBy] || "";
      const compare = aValue.localeCompare(bValue, undefined, {
        sensitivity: "base",
      });
      return sortOrder === SortOrder.ASC ? compare : -compare;
    });
  }, [serviceTickets, sortBy, sortOrder]);

  const getInventoryIcon = (itemType?: InventoryType) => {
    switch (itemType) {
      case InventoryType.PACKAGE:
        return <Package size={18} className="text-amber-500" />;
      default:
        return <Package size={18} className="text-green-500" />;
    }
  };

  const formatPackingType = (packingType?: PackingTypes) => {
    if (!packingType) return "N/A";

    const packingMap: Record<PackingTypes, string> = {
      [PackingTypes.LOOSE]: "Loose",
      [PackingTypes.SLIPCASE]: "Slipcase",
      [PackingTypes.CB_FOLD]: "Cardboard Fold",
      [PackingTypes.SHADOW_BOX]: "Shadow Box",
      [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
      [PackingTypes.POLY_ONLY]: "Poly Only",
      [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
      [PackingTypes.CRATE]: "Crate",
      [PackingTypes.SLATCRATE]: "Slat Crate",
      [PackingTypes.PALLET]: "Pallet",
      [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
      [PackingTypes.CBIN]: "C-Bin",
      [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
      [PackingTypes.BOX]: "Box",
      [PackingTypes.CUSTOM]: "Custom",
      [PackingTypes.T_Frame]: "T Frame",
      [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
      [PackingTypes.POLY_FOAM]: "Poly/Foam",
      [PackingTypes.POLY_FOAM_CB]: "Poly/Foam/CB",
    };

    return packingMap[packingType] || packingType;
  };

  const getStatusColor = (status?: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "bg-green-100 text-green-800";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "bg-blue-100 text-blue-800";
      case InventoryStatusCode.NO_POSSESSION:
        return "bg-orange-100 text-orange-800";
      case InventoryStatusCode.RELEASED:
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3 md:space-y-4">
        <Title level={3} className="!mb-0 text-lg md:text-xl">
          My Inventory
        </Title>
        <div className="text-center py-6 md:py-8">
          <Spin size="large" />
          <Text
            type="secondary"
            className="block mt-3 md:mt-4 text-sm md:text-base"
          >
            Loading inventory...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <Title level={3} className="!mb-0 text-lg md:text-xl">
        My Inventory
      </Title>

      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.INVENTORY_REF)}
              >
                <div className="flex items-center">
                  <span>ID</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.INVENTORY_REF &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.INVENTORY_REF &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort("clientInternalRef")}
              >
                <div className="flex items-center">
                  <span>Client Ref #</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === "clientInternalRef" &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === "clientInternalRef" &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.TITLE)}
              >
                <div className="flex items-center">
                  <span>Title</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.TITLE &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.TITLE &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.ARTIST_NAME)}
              >
                <div className="flex items-center">
                  <span>Artist</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.ARTIST_NAME &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.ARTIST_NAME &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th className="px-4 py-3">Medium</th>
              <th className="px-4 py-3">Dimensions</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {allInventories.map((item) => (
              <tr
                key={item.id}
                className={`hover:bg-gray-50 cursor-pointer ${
                  selectedInventory?.id === item.id ? "bg-blue-50" : ""
                }`}
                onClick={() => handleSelectInventory(item)}
              >
                <td className="px-4 py-3 text-sm">{item.inventoryRef}</td>
                <td className="px-4 py-3 font-medium">{item.clientInternalRef}</td>
                <td className="px-4 py-3 font-medium">{item.title}</td>
                <td className="px-4 py-3 text-sm">{item.artistName}</td>
                <td className="px-4 py-3 text-sm">{item.medium}</td>
                <td className="px-4 py-3 text-sm">{formatDimensions(item)}</td>
                <td className="px-4 py-3 text-sm">
                  ${item.value?.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                      item.status
                    )}`}
                  >
                    {capitalize(item.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{item.location?.name}</td>
                <td className="px-4 py-3">
                  <div className="flex space-x-1">
                    <button
                      className="p-1 text-gray-400 hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectInventory(item);
                      }}
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {allInventories.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-gray-500">
                  No inventory items found in your assignments
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-medium mb-4">Inventory Details</h3>
        <div className="bg-gray-50 border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="col-span-1">
              <div className="bg-white border rounded-lg p-4 h-64">
                {selectedInventory &&
                selectedInventory.images &&
                selectedInventory.images.length > 0 ? (
                  <div className="relative h-56 inventory-carousel">
                    <Carousel
                      autoplay
                      autoplaySpeed={3000}
                      className="h-56"
                      arrows
                    >
                      {selectedInventory.images.map((image, index) => (
                        <div
                          key={image.id || index}
                          className="h-56 flex items-center justify-center"
                        >
                          <img
                            src={image.url}
                            alt={`${selectedInventory.title} - Image ${
                              index + 1
                            }`}
                            className="max-w-full max-h-56 object-contain object-center"
                          />
                        </div>
                      ))}
                    </Carousel>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-center text-gray-400">
                    <div>
                      <Package size={48} className="mx-auto mb-2" />
                      <p>No preview available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <div className="bg-white border rounded-lg p-4 h-64 overflow-auto">
                {selectedInventory ? (
                  <div>
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-lg mb-3">
                        {selectedInventory.title || "Untitled Item"}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                          selectedInventory.status
                        )}`}
                      >
                        {capitalize(selectedInventory.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="flex items-center mb-2">
                          {getInventoryIcon(selectedInventory.itemType)}
                          <span className="font-medium ml-2">
                            {capitalize(selectedInventory.itemType)}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Artist:</span>{" "}
                          {selectedInventory.artistName || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Ref ID:</span>{" "}
                          {selectedInventory.clientInternalRef || "Not assigned"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Medium:</span>{" "}
                          {selectedInventory.medium || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Dimensions:</span>{" "}
                          {formatDimensions(selectedInventory)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Value:</span> $
                          {selectedInventory.value?.toLocaleString() ||
                            "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Packing:</span>{" "}
                          {formatPackingType(selectedInventory.packingType)}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Location:</span>{" "}
                          {selectedInventory.location?.name || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Parts:</span>{" "}
                          {selectedInventory.parts || 1}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Weight:</span>{" "}
                          {selectedInventory.weight
                            ? `${selectedInventory.weight} lbs`
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="font-medium mb-3">
                      Select an item to view details
                    </h4>
                    <p className="text-gray-500">
                      Click on any inventory item to see detailed information
                      including condition reports, handling instructions, and
                      movement history.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};